/** github:org/repo#123 → its parts and GitHub URL */
export function parsePrRef(ref: string): { org: string; repo: string; number: string; url: string } | null {
  const m = /^github:([^/]+)\/([^#]+)#(\d+)$/.exec(ref);
  return m ? { org: m[1], repo: m[2], number: m[3], url: `https://github.com/${m[1]}/${m[2]}/pull/${m[3]}` } : null;
}

/** gitlab:group/project!12 → its project and number; the reference has no host, the link comes from the review */
export function parseMrRef(ref: string): { project: string; iid: string } | null {
  // A path of names only: the project goes into the glab commands of a session's first message
  const m = /^gitlab:([\w.-]+(?:\/[\w.-]+)+)!(\d+)$/.exec(ref);
  return m ? { project: m[1], iid: m[2] } : null;
}

/**
 * github:org/repo#12 of a ticket → its parts and its GitHub issue URL (issues and PRs share the numbering);
 * jira:ticket/site/KEY of a Jira ticket implemented from aidev (ADR-034 step 5b) → its key, as `number`, and its Jira URL;
 * its `org` and `repo` are empty, the reference does not carry them (the ticket's `repo` does)
 */
export function parseIssueRef(ref: string): { org: string; repo: string; number: string; url: string; key?: string; gitlab?: true } | null {
  const pr = parsePrRef(ref);
  if (pr) return { ...pr, url: `https://github.com/${pr.org}/${pr.repo}/issues/${pr.number}` };
  // A GitLab issue (ADR-041 step 3): its project, as `org`; its link is the ticket's `issueUrl`, the reference has no host
  const g = /^gitlab:(.+)#(\d+)$/.exec(ref);
  if (g) return { org: g[1], repo: '', number: g[2], url: '', gitlab: true };
  const j = /^jira:ticket\/([a-z0-9-]+)\/([A-Z][A-Z0-9_]*-\d+)$/.exec(ref);
  return j ? { org: '', repo: '', number: j[2], key: j[2], url: `https://${j[1]}.atlassian.net/browse/${j[2]}` } : null;
}

/** `org/repo#12`, or `INFRA-9`: a ticket's reference as people read it */
export function ticketLabel(ref: string): string {
  const issue = parseIssueRef(ref);
  return issue?.key ?? ref.replace(/^git(hub|lab):/, '');
}

const BRANCH_TYPES = ['feat', 'fix', 'chore', 'docs', 'refactor', 'test', 'ci', 'perf', 'build', 'style', 'revert'];

/**
 * The branch Open the PR creates, named for the change from its title, with the ticket's id: `feat(api): handle an
 * empty tag list` on #891 → `feat/handle-an-empty-tag-list-891`. The API's rules (src/services/branch-name.ts).
 */
export function ticketBranch(ref: string, title: string, step = 1): string {
  const issue = parseIssueRef(ref);
  const id = `${issue?.key ?? issue?.number ?? ''}${step > 1 ? `-step-${step}` : ''}`;
  const conventional = /^([a-z]+)(?:\([^)]*\))?!?:\s*(.*)$/i.exec(title.trim());
  const typed = !!conventional && BRANCH_TYPES.includes(conventional[1].toLowerCase());
  const subject = (typed ? conventional![2] : title).replace(/\[[^\]]*\]/g, ' ');
  const type = typed ? conventional![1].toLowerCase() : /^\s*fix/i.test(subject) ? 'fix' : 'feat';
  let slug = subject.normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  if (slug.length > 48) slug = slug.slice(0, 49).includes('-') ? slug.slice(0, 49).replace(/-[^-]*$/, '') : slug.slice(0, 48);
  const tail = id.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  return `${type}/${[slug, tail].filter(Boolean).join('-')}`;
}

/** The words of a ticket's tracker: a GitLab issue's change is a merge request (!9), any other a pull request (#9) */
export function trackerWords(ref: string): { site: string; request: string; Request: string; short: string; sigil: string } {
  return ref.startsWith('gitlab:')
    ? { site: 'GitLab', request: 'merge request', Request: 'Merge request', short: 'MR', sigil: '!' }
    : { site: 'GitHub', request: 'pull request', Request: 'Pull request', short: 'PR', sigil: '#' };
}
